<template>
  <div class="preika-dashboard">
    <section class="hero-panel">
      <div class="hero-panel__glow hero-panel__glow--one"></div>
      <div class="hero-panel__glow hero-panel__glow--two"></div>

      <div class="hero-panel__content">
        <div class="hero-brand">
          <img
            src="/img/avatars/preika_title.png"
            alt="PREIKA"
            class="hero-brand__logo"
          >
          <span class="hero-brand__badge">WORKPLACE PLATFORM</span>
        </div>

        <p class="hero-panel__eyebrow">Good {{ dayPart }}, {{ displayUserName }}</p>
        <h1 class="hero-panel__title">
          今日の仕事を、<br>
          <span>ひとつの画面から。</span>
        </h1>
        <p class="hero-panel__description">
          勤怠・申請・月次報告をスムーズに。
          PREIKAが毎日の業務をシンプルにつなぎます。
        </p>

        <div class="hero-panel__meta">
          <div class="hero-meta-item">
            <CIcon name="cil-calendar"/>
            <span>{{ formattedDate }}</span>
          </div>
          <div class="hero-meta-item">
            <CIcon name="cil-clock"/>
            <span>{{ formattedTime }}</span>
          </div>
        </div>
      </div>

      <div class="hero-visual" aria-hidden="true">
        <div class="hero-visual__orbit hero-visual__orbit--one"></div>
        <div class="hero-visual__orbit hero-visual__orbit--two"></div>
        <div class="hero-visual__card hero-visual__card--rear">
          <span class="visual-line visual-line--short"></span>
          <span class="visual-line"></span>
          <span class="visual-line visual-line--middle"></span>
        </div>
        <div class="hero-visual__card hero-visual__card--front">
          <div class="visual-card__header">
            <span class="visual-avatar">P</span>
            <span class="visual-status"></span>
          </div>
          <div class="visual-chart">
            <span style="height: 36%"></span>
            <span style="height: 58%"></span>
            <span style="height: 45%"></span>
            <span style="height: 78%"></span>
            <span style="height: 64%"></span>
            <span style="height: 92%"></span>
          </div>
          <div class="visual-card__footer">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </section>

    <section class="dashboard-grid dashboard-grid--top">
      <article class="dashboard-card attendance-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">ATTENDANCE</p>
            <h2>打刻</h2>
          </div>
          <span
            class="status-pill"
            :class="{
              'status-pill--active': attendanceStatus === 'WORKING' && !stampingStatusError,
              'status-pill--break': attendanceStatus === 'BREAK' && !stampingStatusError,
              'status-pill--error': stampingStatusError
            }"
          >
            <span class="status-pill__dot"></span>
            {{ attendanceStatusLabel }}
          </span>
        </div>

        <div class="attendance-time">
          <strong>{{ formattedTime }}</strong>
          <span>{{ formattedDateWithWeekday }}</span>
        </div>

        <div class="attendance-actions">
          <button
            type="button"
            class="attendance-button attendance-button--start"
            :disabled="loading || stampingStatusLoading || !canStartAttendance"
            @click="handleTime('start')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-play"/></span>
            <span><strong>出勤</strong><small>業務を開始する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--end"
            :disabled="loading || stampingStatusLoading || !canEndAttendance"
            @click="handleTime('end')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-media-stop"/></span>
            <span><strong>退勤</strong><small>業務を終了する</small></span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--break"
            :disabled="loading || stampingStatusLoading || !canStartBreak"
            @click="handleTime('breakStart')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-coffee"/></span>
            <span>
              <strong>休憩開始</strong>
              <small>{{ breakStartDescription }}</small>
            </span>
          </button>

          <button
            type="button"
            class="attendance-button attendance-button--return"
            :disabled="loading || stampingStatusLoading || !canEndBreak"
            @click="handleTime('breakEnd')"
          >
            <span class="attendance-button__icon"><CIcon name="cil-loop"/></span>
            <span>
              <strong>休憩終了</strong>
              <small>{{ breakEndDescription }}</small>
            </span>
          </button>
        </div>

        <section
          v-if="stampingStatusLoaded"
          class="attendance-stamp-result"
          aria-labelledby="attendance-stamp-result-title"
          aria-live="polite"
        >
          <div class="attendance-stamp-result__header">
            <div>
              <p>STAMP RESULT</p>
              <h3 id="attendance-stamp-result-title">打刻結果</h3>
            </div>
            <span v-if="businessDate" class="attendance-stamp-result__date">
              勤務日 {{ formatBusinessDate(businessDate) }}
            </span>
          </div>

          <div class="attendance-stamp-result__grid">
            <div class="attendance-stamp-result__item attendance-stamp-result__item--start">
              <span class="attendance-stamp-result__label">出勤</span>
              <strong>{{ startTime || '--:--' }}</strong>
              <small>勤務開始</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--end">
              <span class="attendance-stamp-result__label">退勤</span>
              <strong>{{ endTimeDisplay }}</strong>
              <small>勤務終了</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--break">
              <span class="attendance-stamp-result__label">通常休憩</span>
              <strong>{{ breakTime || '00:00' }}</strong>
              <small>05:00〜22:00</small>
            </div>

            <div class="attendance-stamp-result__item attendance-stamp-result__item--night">
              <span class="attendance-stamp-result__label">夜勤休憩</span>
              <strong>{{ nightRest || '00:00' }}</strong>
              <small>22:00〜05:00</small>
            </div>
          </div>
        </section>

        <div
          v-else-if="stampingStatusError"
          class="attendance-status-error"
          role="alert"
        >
          <div>
            <strong>打刻情報を取得できませんでした</strong>
            <small>安全のため打刻ボタンを無効にしています。</small>
          </div>
          <button
            type="button"
            :disabled="stampingStatusLoading"
            @click="fetchStampingStatus"
          >
            再取得
          </button>
        </div>

        <div v-if="loading" class="attendance-loading">
          <span class="attendance-loading__spinner"></span>
          打刻情報を送信しています
        </div>
      </article>

      <article class="dashboard-card quick-card">
        <div class="card-heading">
          <div>
            <p class="card-heading__eyebrow">QUICK ACCESS</p>
            <h2>よく使うメニュー</h2>
          </div>
          <CIcon name="cil-apps" class="card-heading__icon"/>
        </div>

        <div class="quick-menu">
          <router-link
            v-for="item in quickMenu"
            :key="item.label"
            :to="item.to"
            class="quick-menu__item"
          >
            <span class="quick-menu__icon" :class="'quick-menu__icon--' + item.tone">
              <CIcon :name="item.icon"/>
            </span>
            <span class="quick-menu__text">
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
            <CIcon name="cil-chevron-right" class="quick-menu__arrow"/>
          </router-link>
        </div>
      </article>
    </section>

    <section class="metric-grid">
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--blue">
        <span class="metric-card__icon"><CIcon name="cil-inbox"/></span>
        <span class="metric-card__body"><small>未申請</small><strong>{{ actionCounts.unsubmittedCount || 0 }}</strong><span>今月の提出物</span></span>
      </router-link>
      <router-link to="/submission_approval/report_list" class="metric-card metric-card--orange">
        <span class="metric-card__icon"><CIcon name="cil-warning"/></span>
        <span class="metric-card__body"><small>差戻し</small><strong>{{ actionCounts.rejectedCount || 0 }}</strong><span>再対応が必要</span></span>
      </router-link>
      <router-link to="/submission_approval/submission_management" class="metric-card metric-card--purple">
        <span class="metric-card__icon"><CIcon name="cil-check-circle"/></span>
        <span class="metric-card__body"><small>承認待ち</small><strong>{{ actionCounts.approvalWaitingCount || 0 }}</strong><span>確認が必要な申請</span></span>
      </router-link>
      <div class="metric-card metric-card--green">
        <span class="metric-card__icon"><CIcon name="cil-clock"/></span>
        <span class="metric-card__body"><small>今月の勤務時間</small><strong class="metric-card__time">{{ formatDuration(monthlySummary.workTime) }}</strong><span>{{ monthlySummary.attendanceDays || 0 }}日 出勤</span></span>
      </div>
    </section>

    <section class="preika-insight-layout preika-insight-layout--personal">
      <DashboardActionCenter
        :items="actionItems"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
      <DashboardWorkProjection
        :projection="workProjection"
        :management-view="managementView"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <section
      v-if="managementView"
      class="preika-insight-layout preika-insight-layout--management"
    >
      <DashboardCloseCockpit
        :summary="closeCockpit"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
      <DashboardApprovalAging
        :aging="approvalAging"
        :loading="insightLoading"
        :error="insightError"
        @retry="fetchDashboardInsights"
      />
    </section>

    <section class="dashboard-grid dashboard-grid--overview">
      <article class="dashboard-card monthly-summary-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">MONTHLY PERFORMANCE</p><h2>今月の勤務サマリー</h2></div>
          <span class="summary-month">{{ currentYearMonth }}</span>
        </div>
        <div class="summary-list">
          <div v-for="item in summaryItems" :key="item.label" class="summary-item">
            <span class="summary-item__label">{{ item.label }}</span><strong>{{ item.value }}</strong>
          </div>
        </div>
      </article>

      <article class="dashboard-card monthly-status-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">MONTHLY STATUS</p><h2>月次提出状況</h2></div>
          <router-link to="/submission_approval/report_list" class="text-link">一覧を見る<CIcon name="cil-arrow-right"/></router-link>
        </div>
        <div class="document-status-list">
          <div v-for="document in monthlyDocuments" :key="document.documentType" class="document-status">
            <span class="document-status__icon"><CIcon :name="document.icon"/></span>
            <span class="document-status__name">{{ document.documentName }}</span>
            <span class="document-status__badge" :class="'document-status__badge--' + document.tone">{{ document.statusName }}</span>
          </div>
        </div>
      </article>
    </section>

    <section class="dashboard-grid dashboard-grid--middle">
      <article class="dashboard-card notice-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">INFORMATION</p><h2>お知らせ</h2></div>
          <span class="notice-count">{{ notices.length }} NEWS</span>
        </div>
        <div class="notice-list">
          <div v-for="notice in notices" :key="notice.date + notice.title" class="notice-item">
            <div class="notice-item__date">{{ notice.date }}</div>
            <div class="notice-item__content">
              <span class="notice-item__category" :class="'notice-item__category--' + notice.type">{{ notice.category }}</span>
              <p>{{ notice.title }}</p>
            </div>
          </div>
        </div>
      </article>

      <article class="dashboard-card submission-card">
        <div class="card-heading">
          <div><p class="card-heading__eyebrow">SUBMISSION GUIDE</p><h2>提出ガイド</h2></div>
          <CIcon name="cil-paper-plane" class="card-heading__icon"/>
        </div>
        <div class="submission-guide">
          <div class="submission-guide__item submission-guide__item--monthly">
            <span class="submission-guide__number">02</span>
            <div><strong>第2営業日まで</strong><p>勤務表・業務報告・通勤費・経費</p></div>
          </div>
          <div class="submission-guide__item submission-guide__item--anytime">
            <span class="submission-guide__number"><CIcon name="cil-bell"/></span>
            <div><strong>わかり次第、早めに</strong><p>休暇・予定外時間外・休日出勤</p></div>
          </div>
        </div>
        <router-link to="/submission_approval/report_list" class="submission-link">承認申請一覧を確認<CIcon name="cil-arrow-right"/></router-link>
      </article>
    </section>

    <section class="dashboard-card calendar-card">
      <div class="card-heading calendar-card__heading">
        <div><p class="card-heading__eyebrow">SCHEDULE</p><h2>社内カレンダー</h2></div>
      </div>
      <div class="calendar-frame">
        <iframe
          src="https://calendar.google.com/calendar/embed?height=600&wkst=1&ctz=Asia%2FTokyo&bgcolor=%23ffffff&src=YWtpLWNvcnAuY29tXzFtdDllbjlrbWRvMTlicGM0azV2NXNhZzY0QGdyb3VwLmNhbGVuZGFyLmdvb2dsZS5jb20&src=amEuamFwYW5lc2UjaG9saWRheUBncm91cC52LmNhbGVuZGFyLmdvb2dsZS5jb20&color=%23D50000&color=%230B8043"
          title="PREIKA 社内カレンダー"
          frameborder="0"
          scrolling="no"
        ></iframe>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import ConstList from './../ConstList'
import { normalizeTimeValue } from '../utils/timeValue'
import { isDashboardManagementAuthority } from '../utils/dashboardInsights'
import DashboardActionCenter from '../common/dashboard/DashboardActionCenter'
import DashboardWorkProjection from '../common/dashboard/DashboardWorkProjection'
import DashboardCloseCockpit from '../common/dashboard/DashboardCloseCockpit'
import DashboardApprovalAging from '../common/dashboard/DashboardApprovalAging'

export default {
  name: 'Dashboard',

  components: {
    DashboardActionCenter,
    DashboardWorkProjection,
    DashboardCloseCockpit,
    DashboardApprovalAging
  },

  data () {
    return {
      now: new Date(), timerId: null, loading: false, submitFlg: false,
      useProjectBreakFlg: true, userName: '',
      attendanceStatus: 'NOT_STARTED', businessDate: '',
      startTime: '', endTime: '',
      breakStartedAt: '', breakTime: '00:00', nightRest: '00:00',
      stampingStatusLoaded: false, stampingStatusLoading: false,
      stampingStatusError: false,
      insightLoading: false, insightError: false,
      authorityName: '', managementView: false, actionItems: [],
      workProjection: {}, closeCockpit: {}, approvalAging: {},
      actionCounts: {}, monthlySummary: {}, submissions: [],
      quickMenu: [
        { label: '勤務表', description: '日々の勤務実績を入力', to: '/report/roster', icon: 'cil-calendar-check', tone: 'blue' },
        { label: '業務報告', description: '月次の業務内容を報告', to: '/report/monthlyreport', icon: 'cil-description', tone: 'navy' },
        { label: '経費精算', description: '業務経費を登録・申請', to: '/payoff/expense', icon: 'cil-wallet', tone: 'green' },
        { label: '通勤費精算', description: '通勤経路・交通費を登録', to: '/payoff/travelCost', icon: 'cil-bus-alt', tone: 'cyan' },
        { label: '休暇申請', description: '有休・代休・特休を申請', to: '/paidandsubstitutes/list', icon: 'cil-sun', tone: 'orange' },
        { label: '承認申請一覧', description: '提出状況を確認', to: '/submission_approval/report_list', icon: 'cil-send', tone: 'purple' },
        { label: '承認待ち一覧', description: '確認が必要な申請', to: '/submission_approval/submission_management', icon: 'cil-check-circle', tone: 'cyan' }
      ],
      notices: [
        { date: '2025.09.10', category: 'UPDATE', type: 'update', title: '各種申請でステータスが確認できるようになりました。' },
        { date: '2025.09.10', category: 'UPDATE', type: 'update', title: '休暇申請の連絡先入力を任意項目へ変更しました。' },
        { date: 'NOTICE', category: 'SYSTEM', type: 'system', title: '月次提出期間外に予告なくメンテナンスを実施することがあります。' }
      ]
    }
  },
  computed: {
    displayUserName () { return this.userName || 'メンバー' },
    dayPart () { const hour = this.now.getHours(); if (hour < 11) return 'morning'; if (hour < 18) return 'afternoon'; return 'evening' },
    formattedDate () { return moment(this.now).format('YYYY年 M月 D日') },
    formattedDateWithWeekday () { const weekdays = ['日', '月', '火', '水', '木', '金', '土']; return moment(this.now).format('YYYY年 M月 D日') + '（' + weekdays[this.now.getDay()] + '）' },
    formattedTime () { return moment(this.now).format('HH:mm:ss') },
    stampingTimestamp () { return moment(this.now).format('YYYY/MM/DD HH:mm') },
    currentYearMonth () { return moment(this.now).format('YYYY年M月') },
    attendanceStatusLabel () {
      if (this.stampingStatusError) return '取得失敗'
      return ({
        NOT_STARTED: '未打刻',
        WORKING: '業務中',
        BREAK: '休憩中',
        FINISHED: '退勤済み'
      })[this.attendanceStatus] || '未打刻'
    },
    endTimeDisplay () {
      if (!this.endTime) return '--:--'
      if (!this.startTime) return this.endTime

      const start = moment(this.startTime, 'HH:mm', true)
      const end = moment(this.endTime, 'HH:mm', true)
      if (!start.isValid() || !end.isValid()) return this.endTime

      return end.isBefore(start)
        ? `${this.endTime}（翌日）`
        : this.endTime
    },
    canStartAttendance () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'NOT_STARTED'
    },
    canEndAttendance () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'WORKING'
    },
    canStartBreak () {
      return this.stampingStatusLoaded &&
        this.attendanceStatus === 'WORKING' &&
        !this.useProjectBreakFlg
    },
    canEndBreak () {
      return this.stampingStatusLoaded && this.attendanceStatus === 'BREAK'
    },
    breakStartLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      return start.format('MM/DD HH:mm')
    },
    breakElapsedLabel () {
      if (!this.breakStartedAt) return ''
      const start = moment(this.breakStartedAt, 'YYYY/MM/DD HH:mm', true)
      if (!start.isValid()) return ''
      const minutes = Math.max(0, moment(this.now).diff(start, 'minutes'))
      const hours = Math.floor(minutes / 60)
      const restMinutes = String(minutes % 60).padStart(2, '0')
      return `${hours}:${restMinutes}`
    },
    breakStartDescription () {
      if (this.useProjectBreakFlg) return 'プロジェクト設定で自動'
      if (this.attendanceStatus === 'BREAK') return '現在休憩中です'
      if (this.attendanceStatus !== 'WORKING') return '出勤後に利用可能'
      return '休憩を記録する'
    },
    breakEndDescription () {
      if (this.attendanceStatus !== 'BREAK') return '休憩開始後に利用可能'
      const start = this.breakStartLabel ? `${this.breakStartLabel} 開始` : '休憩中'
      const elapsed = this.breakElapsedLabel ? `（${this.breakElapsedLabel}経過）` : ''
      return start + elapsed
    },
    summaryItems () {
      return [
        { label: '総勤務時間', value: this.formatDuration(this.monthlySummary.workTime) },
        { label: '時間外', value: this.formatDuration(this.monthlySummary.overTime) },
        { label: '休日勤務', value: this.formatDuration(this.monthlySummary.holidayTime) },
        { label: '深夜勤務', value: this.formatDuration(this.monthlySummary.nightTime) },
        { label: '有休使用', value: (this.monthlySummary.paidHolidayUsed || 0) + '日' },
        { label: '有休残数', value: (this.monthlySummary.paidHolidayRemain || 0) + '日' }
      ]
    },
    monthlyDocuments () {
      const definitions = [
        { documentType: '01', documentName: '勤務表', icon: 'cil-calendar-check' },
        { documentType: '02', documentName: '業務報告', icon: 'cil-description' },
        { documentType: '03', documentName: '経費', icon: 'cil-wallet' },
        { documentType: '04', documentName: '通勤費', icon: 'cil-bus-alt' }
      ]
      return definitions.map(definition => {
        const status = this.submissions.find(item => String(item.documentType) === definition.documentType)
        return { ...definition, statusName: status ? status.statusName : '未登録', tone: this.getStatusTone(status ? String(status.status) : '') }
      })
    }
  },
  mounted () {
    this.timerId = window.setInterval(() => { this.now = new Date() }, 1000)
    this.fetchDashboard()
    this.fetchDashboardAuthority()
    this.fetchDashboardInsights()
    this.fetchProjectBreakSetting()
    this.fetchStampingStatus()
  },
  beforeDestroy () { if (this.timerId) window.clearInterval(this.timerId) },
  methods: {
    formatDuration (value) { if (!value) return '00:00'; return String(value).substring(0, 5) },
    getStatusTone (status) { return ({ '1': 'neutral', '2': 'pending', '3': 'rejected', '4': 'approved' })[status] || 'empty' },
    fetchDashboard () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/dashboard').then(response => {
        const data = response.data || {}
        this.userName = data.userName || ''
        this.actionCounts = data.actionCounts || {}
        this.monthlySummary = data.monthlySummary || {}
        this.submissions = data.submissions || []
      }).catch(error => console.error('ダッシュボード情報取得失敗:', error))
    },
    fetchDashboardAuthority () {
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/authority')
        .then(response => {
          this.authorityName = String(response.data || '').trim()
          this.managementView = isDashboardManagementAuthority(this.authorityName)
        })
        .catch(error => {
          console.error('ダッシュボード権限情報取得失敗:', error)
        })
    },
    fetchDashboardInsights () {
      if (this.insightLoading) return Promise.resolve()

      this.insightLoading = true
      this.insightError = false

      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/dashboard/insights')
        .then(response => {
          const data = response.data || {}
          this.authorityName = String(data.authorityName || this.authorityName || '').trim()
          this.managementView = data.managementView === true ||
            isDashboardManagementAuthority(this.authorityName)
          this.actionItems = Array.isArray(data.actionItems) ? data.actionItems : []
          this.workProjection = data.workProjection || {}
          this.closeCockpit = data.closeCockpit || {}
          this.approvalAging = data.approvalAging || {}
          this.insightError = false
        })
        .catch(error => {
          this.insightError = true
          console.error('ダッシュボード判断支援情報取得失敗:', error)
        })
        .finally(() => { this.insightLoading = false })
    },
    fetchProjectBreakSetting () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/initial')
        .then(response => { this.useProjectBreakFlg = response.data === true })
        .catch(error => console.error('休憩自動設定フラグ取得失敗:', error))
    },
    fetchStampingStatus () {
      this.stampingStatusLoading = true
      this.stampingStatusError = false
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/stamping/status')
        .then(response => {
          const status = response.data || {}
          this.attendanceStatus = status.status || 'NOT_STARTED'
          this.businessDate = status.businessDate || ''
          this.startTime = status.startTime || ''
          this.endTime = status.endTime || ''
          this.breakStartedAt = status.breakStartedAt || ''
          this.breakTime = status.breakTime || '00:00'
          this.nightRest = normalizeTimeValue(
            status.nightRest,
            '00:00'
          )
          this.stampingStatusLoaded = true
          this.stampingStatusError = false
        })
        .catch(error => {
          this.stampingStatusLoaded = false
          this.stampingStatusError = true
          console.error('打刻状態取得失敗:', error)
        })
        .finally(() => { this.stampingStatusLoading = false })
    },
    formatBusinessDate (value) {
      const date = moment(value, 'YYYY-MM-DD', true)
      return date.isValid() ? date.format('YYYY/MM/DD') : value
    },
    handleTime (type) {
      if (this.submitFlg || this.loading || this.stampingStatusLoading) return
      const payload = {
        button_type: type,
        timestamp: this.stampingTimestamp
      }
      this.submitFlg = true
      this.loading = true
      axios.post(process.env.VUE_APP_API_ORIGIN + '/stamping/update', payload)
        .then(() => Promise.all([
          this.fetchStampingStatus(),
          this.fetchDashboard(),
          this.fetchDashboardInsights()
        ]))
        .then(() => { alert(ConstList.SUCCESS_STAMPING) })
        .catch(error => {
          console.error('打刻送信失敗:', error)
          alert(ConstList.SYSTEM_COMMUNICATION_ERROR)
          return this.fetchStampingStatus()
        })
        .finally(() => { this.submitFlg = false; this.loading = false })
    }
  }
}
</script>

<style scoped>
.preika-dashboard{--preika-primary:#0a66b2;--preika-navy:#0c2642;--preika-ink:#172b3f;--preika-muted:#6f8193;--preika-border:#e5edf4;padding:10px 4px 36px;color:var(--preika-ink)}
.hero-panel{position:relative;display:flex;min-height:320px;margin-bottom:24px;overflow:hidden;border-radius:24px;background:linear-gradient(120deg,#064f91 0%,#0b70bc 48%,#0a5ca4 100%);box-shadow:0 24px 60px rgba(5,66,118,.24)}
.hero-panel__content{position:relative;z-index:3;width:60%;padding:42px 52px}.hero-brand{display:flex;align-items:center;margin-bottom:28px}.hero-brand__logo{width:150px;max-height:42px;margin-right:16px;object-fit:contain;filter:brightness(0) invert(1)}.hero-brand__badge{padding:6px 10px;border:1px solid rgba(255,255,255,.26);border-radius:99px;color:rgba(255,255,255,.8);font-size:10px;font-weight:700;letter-spacing:.13em}.hero-panel__eyebrow{margin:0 0 10px;color:#a9dcff;font-size:14px;font-weight:700}.hero-panel__title{margin:0;color:#fff;font-size:38px;font-weight:700;line-height:1.28}.hero-panel__title span{color:#d9f1ff}.hero-panel__description{max-width:580px;margin:18px 0 24px;color:rgba(255,255,255,.78);font-size:14px;line-height:1.9}.hero-panel__meta{display:flex;gap:12px}.hero-meta-item{display:inline-flex;align-items:center;gap:8px;padding:9px 14px;border:1px solid rgba(255,255,255,.15);border-radius:10px;background:rgba(255,255,255,.08);color:#fff;font-size:13px}
.hero-visual{position:relative;z-index:2;flex:1}.hero-visual__orbit{position:absolute;border:1px solid rgba(255,255,255,.13);border-radius:50%}.hero-visual__orbit--one{top:35px;right:18px;width:290px;height:290px}.hero-visual__orbit--two{top:82px;right:62px;width:200px;height:200px}.hero-visual__card{position:absolute;border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.94);box-shadow:0 28px 55px rgba(0,27,59,.28)}.hero-visual__card--rear{top:58px;right:32px;width:230px;height:145px;padding:28px 24px;border-radius:18px;transform:rotate(8deg);opacity:.8}.hero-visual__card--front{top:87px;right:110px;width:245px;height:190px;padding:20px;border-radius:20px;transform:rotate(-4deg)}.visual-line{display:block;width:100%;height:9px;margin-bottom:14px;border-radius:20px;background:#dce8f1}.visual-line--short{width:34%;background:#83c9f4}.visual-line--middle{width:72%}.visual-card__header{display:flex;align-items:center;justify-content:space-between}.visual-avatar{display:inline-flex;width:38px;height:38px;align-items:center;justify-content:center;border-radius:12px;background:#0871be;color:#fff;font-weight:800}.visual-status{width:62px;height:8px;border-radius:20px;background:#dfeaf2}.visual-chart{display:flex;height:85px;align-items:flex-end;gap:10px;margin-top:20px}.visual-chart span{flex:1;border-radius:7px 7px 3px 3px;background:linear-gradient(180deg,#43b8ee,#0871be)}.visual-card__footer{display:flex;gap:8px;margin-top:14px}.visual-card__footer span{width:42px;height:7px;border-radius:10px;background:#dfe9f1}
.dashboard-grid{display:grid;gap:24px;margin-bottom:24px}.dashboard-grid--top{grid-template-columns:minmax(0,1.12fr) minmax(350px,.88fr)}.dashboard-grid--overview{grid-template-columns:minmax(0,1.08fr) minmax(350px,.92fr)}.dashboard-grid--middle{grid-template-columns:minmax(0,1.06fr) minmax(340px,.94fr)}.dashboard-card{overflow:hidden;border:1px solid var(--preika-border);border-radius:20px;background:#fff;box-shadow:0 12px 32px rgba(19,54,83,.07)}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:28px}.card-heading{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:22px}.card-heading__eyebrow{margin:0 0 4px;color:var(--preika-primary);font-size:10px;font-weight:800;letter-spacing:.16em}.card-heading h2{margin:0;font-size:21px}.card-heading__icon{width:24px;color:#92a9ba}
.status-pill{display:inline-flex;align-items:center;gap:7px;padding:7px 11px;border-radius:99px;background:#f1f5f8;color:#7b8d9b;font-size:11px;font-weight:700}.status-pill__dot{width:7px;height:7px;border-radius:50%;background:#aebbc5}.status-pill--active{background:#e9f7f1;color:#148055}.status-pill--active .status-pill__dot{background:#24ad77}.status-pill--break{background:#fff2e5;color:#b9630c}.status-pill--break .status-pill__dot{background:#e58a27}.status-pill--error{background:#fdeaea;color:#ad3f3f}.status-pill--error .status-pill__dot{background:#d95a5a}.attendance-time{display:flex;align-items:baseline;gap:16px;margin-bottom:22px}.attendance-time strong{color:var(--preika-navy);font-size:38px}.attendance-time span{color:var(--preika-muted);font-size:13px}.attendance-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px}.attendance-button{display:flex;min-height:76px;align-items:center;gap:14px;padding:13px 15px;border:1px solid #e2eaf0;border-radius:14px;background:#fff;text-align:left;transition:.2s}.attendance-button:not(:disabled):hover{border-color:#a8d5ef;box-shadow:0 10px 24px rgba(10,102,178,.12);transform:translateY(-2px)}.attendance-button:disabled{opacity:.5}.attendance-button__icon{display:inline-flex;width:42px;height:42px;align-items:center;justify-content:center;border-radius:12px}.attendance-button strong,.attendance-button small{display:block}.attendance-button small{color:#8293a1;font-size:10px}.attendance-button--start .attendance-button__icon{background:#e5f6ef;color:#159565}.attendance-button--end .attendance-button__icon{background:#e8f2fb;color:#0a66b2}.attendance-button--break .attendance-button__icon{background:#fff2e5;color:#d97a16}.attendance-button--return .attendance-button__icon{background:#eeeafc;color:#6550b9}.attendance-stamp-result{margin-top:18px;padding:15px;border:1px solid #e2ebf2;border-radius:15px;background:linear-gradient(180deg,#fbfdff,#f5f9fc)}.attendance-stamp-result__header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:11px}.attendance-stamp-result__header p{margin:0 0 2px;color:#0a66b2;font-size:8px;font-weight:800;letter-spacing:.14em}.attendance-stamp-result__header h3{margin:0;color:#0c2642;font-size:14px}.attendance-stamp-result__date{padding:5px 8px;border-radius:99px;background:#eaf3fa;color:#527188;font-size:8px;font-weight:700}.attendance-stamp-result__grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px}.attendance-stamp-result__item{position:relative;min-width:0;padding:11px 10px;border:1px solid #e4ebf1;border-radius:11px;background:#fff}.attendance-stamp-result__item::before{position:absolute;top:0;right:10px;left:10px;height:3px;border-radius:0 0 4px 4px;content:''}.attendance-stamp-result__item--start::before{background:#24ad77}.attendance-stamp-result__item--end::before{background:#0a66b2}.attendance-stamp-result__item--break::before{background:#e58a27}.attendance-stamp-result__item--night::before{background:#6550b9}.attendance-stamp-result__label,.attendance-stamp-result__item strong,.attendance-stamp-result__item small{display:block}.attendance-stamp-result__label{margin-top:2px;color:#718594;font-size:8px;font-weight:700}.attendance-stamp-result__item strong{margin:4px 0 1px;overflow:hidden;color:#0c2642;font-size:17px;letter-spacing:.02em;text-overflow:ellipsis;white-space:nowrap}.attendance-stamp-result__item small{color:#93a2ad;font-size:7px}.attendance-status-error{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:18px;padding:14px 15px;border:1px solid #f0cccc;border-radius:13px;background:#fff7f7}.attendance-status-error strong,.attendance-status-error small{display:block}.attendance-status-error strong{color:#9f3939;font-size:11px}.attendance-status-error small{margin-top:3px;color:#9b6a6a;font-size:8px}.attendance-status-error button{padding:7px 11px;border:1px solid #dca8a8;border-radius:9px;background:#fff;color:#a13d3d;font-size:9px;font-weight:700}.attendance-status-error button:disabled{opacity:.5}.attendance-loading{margin-top:16px;color:var(--preika-primary);font-size:12px}
.quick-menu{display:grid;grid-template-columns:1fr 1fr;gap:10px}.quick-menu__item{display:flex;min-height:78px;align-items:center;gap:12px;padding:13px;border:1px solid #e6edf3;border-radius:14px;color:inherit;text-decoration:none}.quick-menu__icon{display:inline-flex;width:40px;height:40px;align-items:center;justify-content:center;border-radius:12px}.quick-menu__icon--blue{background:#e5f2fc;color:#0a66b2}.quick-menu__icon--navy{background:#e9eef4;color:#213e59}.quick-menu__icon--green{background:#e5f6ef;color:#16875f}.quick-menu__icon--orange{background:#fff0df;color:#cc7318}.quick-menu__icon--purple{background:#eee9fb;color:#6b52bd}.quick-menu__icon--cyan{background:#e3f5f7;color:#16808a}.quick-menu__text{flex:1}.quick-menu__text strong,.quick-menu__text small{display:block}.quick-menu__text small{color:#8393a0;font-size:9px}.quick-menu__arrow{width:14px;color:#b0bdc7}
.metric-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:24px}.metric-card{display:flex;min-height:118px;align-items:center;gap:16px;padding:20px;border:1px solid var(--preika-border);border-radius:18px;background:#fff;color:inherit;box-shadow:0 10px 28px rgba(19,54,83,.06);text-decoration:none}.metric-card__icon{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:15px}.metric-card--blue .metric-card__icon{background:#e4f2fc;color:#0a66b2}.metric-card--orange .metric-card__icon{background:#fff0df;color:#cf7315}.metric-card--purple .metric-card__icon{background:#eee9fb;color:#6851b7}.metric-card--green .metric-card__icon{background:#e4f6ed;color:#14835b}.metric-card__body small,.metric-card__body strong,.metric-card__body span{display:block}.metric-card__body small{color:#778997;font-size:10px}.metric-card__body strong{margin:3px 0;font-size:28px}.metric-card__body span{color:#95a3ae;font-size:9px}.metric-card__body .metric-card__time{font-size:23px}
.summary-month{padding:6px 10px;border-radius:8px;background:#eef5fa;color:#607789;font-size:10px}.summary-list{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.summary-item{padding:17px;border-radius:14px;background:#f7f9fb}.summary-item__label{display:block;margin-bottom:8px;color:#788b99;font-size:10px}.summary-item strong{font-size:18px}.text-link{display:inline-flex;align-items:center;gap:6px;color:var(--preika-primary);font-size:10px}.document-status-list{display:grid;gap:9px}.document-status{display:grid;grid-template-columns:38px 1fr auto;align-items:center;gap:12px;padding:12px 14px;border:1px solid #e8eef3;border-radius:13px}.document-status__icon{display:inline-flex;width:36px;height:36px;align-items:center;justify-content:center;border-radius:10px;background:#edf5fa;color:var(--preika-primary)}.document-status__name{font-size:12px;font-weight:700}.document-status__badge{min-width:64px;padding:5px 8px;border-radius:99px;text-align:center;font-size:9px;font-weight:800}.document-status__badge--neutral{background:#f0f3f5;color:#6f7f8b}.document-status__badge--pending{background:#fff3d9;color:#9d6900}.document-status__badge--rejected{background:#fde8e8;color:#b44444}.document-status__badge--approved{background:#e4f6ed;color:#14764f}.document-status__badge--empty{background:#f3f5f7;color:#9aa6ae}
.notice-count{color:#8798a5;font-size:10px}.notice-list{border-top:1px solid #edf2f6}.notice-item{display:grid;grid-template-columns:92px 1fr;gap:18px;padding:17px 2px;border-bottom:1px solid #edf2f6}.notice-item__date{color:#8a9aa7;font-size:11px}.notice-item__content{display:flex;align-items:center;gap:12px}.notice-item__content p{margin:0;font-size:12px}.notice-item__category{padding:4px 8px;border-radius:6px;font-size:9px}.notice-item__category--update{background:#e5f2fc;color:#0a66b2}.notice-item__category--system{background:#fff0df;color:#bc6913}.submission-guide{display:grid;gap:12px}.submission-guide__item{display:flex;align-items:center;gap:16px;padding:17px;border-radius:15px}.submission-guide__item--monthly{background:#eef6fc}.submission-guide__item--anytime{background:#eef8f4}.submission-guide__number{display:inline-flex;width:48px;height:48px;align-items:center;justify-content:center;border-radius:14px;background:#fff;color:var(--preika-primary);font-weight:800}.submission-guide p{margin:0;color:#6f8190;font-size:10px}.submission-link{display:flex;justify-content:space-between;margin-top:17px;padding:12px 15px;border-radius:11px;background:var(--preika-navy);color:#fff;text-decoration:none}.calendar-card__heading{margin:0;padding:25px 28px 20px}.calendar-frame{min-height:520px;border-top:1px solid #e8eef3}.calendar-frame iframe{display:block;width:100%;height:600px;border:0}
@media(max-width:1199px){.hero-panel__content{width:62%;padding:38px}.hero-visual__card--front{right:55px}.dashboard-grid--top,.dashboard-grid--middle,.dashboard-grid--overview{grid-template-columns:1fr}}
@media(max-width:900px){.metric-grid{grid-template-columns:1fr 1fr}}
@media(max-width:767px){.hero-panel{min-height:auto;border-radius:18px}.hero-panel__content{width:100%;padding:30px 24px}.hero-brand{align-items:flex-start;flex-direction:column;gap:10px}.hero-panel__title{font-size:29px}.hero-visual{display:none}.attendance-card,.quick-card,.notice-card,.submission-card,.monthly-summary-card,.monthly-status-card{padding:22px 18px}.attendance-time{align-items:flex-start;flex-direction:column}.attendance-actions,.quick-menu,.metric-grid,.summary-list{grid-template-columns:1fr}.attendance-stamp-result__header{align-items:flex-start;flex-direction:column}.attendance-stamp-result__grid{grid-template-columns:1fr 1fr}.attendance-status-error{align-items:flex-start;flex-direction:column}.attendance-status-error button{width:100%}.notice-item{grid-template-columns:1fr}.notice-item__content{align-items:flex-start;flex-direction:column}}
</style>
